(function ($) {
    const trackedFieldKeys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
        "gclid",
        "fbclid",
        "msclkid",
        "first_utm_source",
        "first_utm_medium",
        "first_utm_campaign",
        "landing_page",
        "conversion_page",
        "referrer",
        "property_id",
        "property_address",
        "device_type"
    ];
    const pageContextFieldKeys = [
        "property_id",
        "property_address",
        "data_layer_id",
        "development",
        "region",
    ];
    const firstTouchMappings = {
        utm_source: "first_utm_source",
        utm_medium: "first_utm_medium",
        utm_campaign: "first_utm_campaign",
    };
    const pushedSubmissionKeys = new Set();
    let isNinjaFormsSubmitResponseBound = false;

    function storeTrackingValue(key, value) {
        if (!key || !value) {
            return;
        }

        localStorage.setItem(key, value);
        document.cookie =
            key +
            "=" +
            encodeURIComponent(value) +
            "; path=/; max-age=" +
            60 * 60 * 24 * 30 +
            "; SameSite=Lax";
    }

    function getDeviceType() {
        const width = window.innerWidth;
        const hasTouch = navigator.maxTouchPoints > 0;

        if (width < 768) {
            return "mobile";
        }

        if (width < 1024 && hasTouch) {
            return "tablet";
        }

        return "desktop";
    }

    function getPageContextValue(key) {
        if (!window.bfsTrackingContext || !pageContextFieldKeys.includes(key)) {
            return null;
        }

        return window.bfsTrackingContext[key] || null;
    }

    function getTrackingValue(key) {
        return getPageContextValue(key) || localStorage.getItem(key);
    }

    function getModelValue(model, key) {
        if (!model) {
            return null;
        }

        if (typeof model.get === "function") {
            return model.get(key);
        }

        return model[key] || null;
    }

    function getFormObjectById(formId) {
        if (!formId) {
            return null;
        }

        const forms = normalizeForms(window.nfForms);

        return forms.find((formObject) => {
            const id = getModelValue(formObject, "id") || getModelValue(formObject, "ID");

            return String(id) === String(formId);
        }) || null;
    }

    function slugify(value) {
        if (!value) {
            return "";
        }

        return String(value)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
    }

    function getFormId(response, formId) {
        if (formId) {
            return formId;
        }

        if (!response || typeof response !== "object") {
            return null;
        }

        if (response.form_id) {
            return response.form_id;
        }

        if (response.data && response.data.form_id) {
            return response.data.form_id;
        }

        if (response.id) {
            return response.id;
        }

        return null;
    }

    function getFormTitle(formId, response) {
        const formObject = getFormObjectById(formId);
        const settings = getModelValue(formObject, "settings");

        return getModelValue(formObject, "title") ||
            getModelValue(settings, "title") ||
            (response && response.data && response.data.settings && response.data.settings.title) ||
            "";
    }

    function getFormType(formId, response) {
        const titleFormType = slugify(getFormTitle(formId, response));

        return titleFormType || "ninja_form";
    }

    function getDataLayerAttribute(key) {
        const context = window.bfsTrackingContext || {};

        return context[key] || getTrackingValue(key) || null;
    }

    function removeEmptyValues(values) {
        return Object.entries(values).reduce((filteredValues, [key, value]) => {
            if (value !== null && typeof value !== "undefined" && String(value).trim() !== "") {
                filteredValues[key] = value;
            }

            return filteredValues;
        }, {});
    }

    function hasSubmissionErrors(response) {
        if (!response || response === "error") {
            return true;
        }

        if (!response.errors) {
            return false;
        }

        if (Array.isArray(response.errors)) {
            return response.errors.length > 0;
        }

        return Object.keys(response.errors).length > 0;
    }

    function pushFormSubmissionToDataLayer(response, formId) {
        if (hasSubmissionErrors(response)) {
            return;
        }

        const resolvedFormId = getFormId(response, formId);
        const responseData = response && response.data ? response.data : {};
        const submissionId = responseData.sub_id || responseData.submission_id || "";
        const submissionKey = [
            resolvedFormId || "unknown",
            submissionId || "latest",
        ].join(":");

        if (pushedSubmissionKeys.has(submissionKey)) {
            return;
        }

        pushedSubmissionKeys.add(submissionKey);

        window.setTimeout(() => {
            pushedSubmissionKeys.delete(submissionKey);
        }, 3000);

        const attributes = removeEmptyValues({
            id: getDataLayerAttribute("data_layer_id") || getDataLayerAttribute("property_id"),
            development: getDataLayerAttribute("development"),
            region: getDataLayerAttribute("region"),
        });

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "formSubmission",
            formType: getFormType(resolvedFormId, response),
            attributes,
        });
    }

    function bindNinjaFormsSubmitResponse() {
        if (
            isNinjaFormsSubmitResponseBound ||
            !window.nfRadio ||
            typeof window.nfRadio.channel !== "function"
        ) {
            return;
        }

        window.nfRadio.channel("forms").on("submit:response", function (response, _textStatus, _jqXHR, formId) {
            pushFormSubmissionToDataLayer(response, formId);
        });

        isNinjaFormsSubmitResponseBound = true;
    }

    function persistQueryParams() {
        const url = new URL(window.location.href);

        trackedFieldKeys.forEach((param) => {
            if (pageContextFieldKeys.includes(param)) {
                return;
            }

            const value = url.searchParams.get(param);

            if (!value) {
                return;
            }

            storeTrackingValue(param, value);
        });

        Object.entries(firstTouchMappings).forEach(([utmKey, firstTouchKey]) => {
            const currentValue = url.searchParams.get(utmKey);
            const existingFirstTouchValue = localStorage.getItem(firstTouchKey);

            if (!currentValue || existingFirstTouchValue) {
                return;
            }

            storeTrackingValue(firstTouchKey, currentValue);
        });

        if (!localStorage.getItem("landing_page")) {
            storeTrackingValue("landing_page", url.pathname + url.search);
        }

        storeTrackingValue("conversion_page", url.pathname + url.search);
        storeTrackingValue("device_type", getDeviceType());

        if (!localStorage.getItem("referrer") && document.referrer) {
            try {
                const referrerUrl = new URL(document.referrer);

                if (referrerUrl.origin !== url.origin) {
                    storeTrackingValue("referrer", document.referrer);
                }
            } catch (_error) {
                storeTrackingValue("referrer", document.referrer);
            }
        }
    }

    function normalizeForms(forms) {
        if (!forms) {
            return [];
        }

        if (Array.isArray(forms)) {
            return forms;
        }

        return Object.values(forms);
    }

    function normalizeFields(fields) {
        if (!fields) {
            return [];
        }

        if (Array.isArray(fields)) {
            return fields;
        }

        if (fields.models && Array.isArray(fields.models)) {
            return fields.models;
        }

        return Object.values(fields);
    }

    function getFieldId(field) {
        if (!field) {
            return null;
        }

        if (typeof field.get === "function") {
            return field.get("id");
        }

        if (typeof field.id !== "undefined") {
            return field.id;
        }

        if (field.attributes && typeof field.attributes.id !== "undefined") {
            return field.attributes.id;
        }

        if (field.settings && typeof field.settings.id !== "undefined") {
            return field.settings.id;
        }

        return null;
    }

    function getFieldKey(field) {
        if (!field) {
            return null;
        }

        if (typeof field.get === "function") {
            return field.get("key");
        }

        if (typeof field.key !== "undefined") {
            return field.key;
        }

        if (field.attributes && typeof field.attributes.key !== "undefined") {
            return field.attributes.key;
        }

        if (field.settings && typeof field.settings.key !== "undefined") {
            return field.settings.key;
        }

        return null;
    }

    function setInputValue(fieldId, value) {
        if (!fieldId || !value) {
            return;
        }

        const $input = $("#nf-field-" + fieldId);

        if (!$input.length) {
            return;
        }

        $input.val(value).trigger("change");
    }

    function populateFieldsFromFormObject(formObject) {
        if (!formObject) {
            return;
        }

        const fields = normalizeFields(formObject.fields);

        fields.forEach((field) => {
            const key = getFieldKey(field);

            if (!trackedFieldKeys.includes(key)) {
                return;
            }

            const value = getTrackingValue(key);

            if (!value) {
                return;
            }

            const fieldId = getFieldId(field);
            setInputValue(fieldId, value);
        });
    }

    function populateTrackingFields() {
        normalizeForms(window.nfForms).forEach((formObject) => {
            populateFieldsFromFormObject(formObject);
        });
    }

    function populateTrackingFieldsFromLayoutView(layoutView) {
        if (!layoutView || !layoutView.model || typeof layoutView.model.get !== "function") {
            populateTrackingFields();
            return;
        }

        populateFieldsFromFormObject({
            fields: layoutView.model.get("fields"),
        });
    }

    persistQueryParams();

    $(populateTrackingFields);
    $(document).on("nfFormReady", function (_event, layoutView) {
        bindNinjaFormsSubmitResponse();
        populateTrackingFieldsFromLayoutView(layoutView);
    });

    $(document).on("nfFormSubmitResponse", function (_event, response, formId) {
        pushFormSubmissionToDataLayer(response, formId);
    });

    bindNinjaFormsSubmitResponse();

    window.setTimeout(populateTrackingFields, 500);
})(jQuery);
